<template>
  <div class="time-picker">
    <el-autocomplete
      ref="autocompleteRef"
      v-model="selectedTime"
      class="autocomplete-input"
      clearable
      :disabled="disabled"
      :placeholder="placeholder || $t(placeholderI18nKey)"
      :popper-class="popperClass"
      :fetch-suggestions="querySearch"
      :prefix-icon="prefixIcon"
      :suffix-icon="suffixIcon"
      @select="handleSelect"
      @change="handleChange"
      @clear="handleClear"
      @focus="handleFocus"
    >
    </el-autocomplete>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'

const reg12Hour = /^(0?[1-9]|1[0-2]):[0-5][0-9](:(?:[0-5][0-9])?|( |:)?(am|pm|AM|PM))?$|(2[0-3]|[01]?[0-9]):[0-5][0-9]$/
const reg24Hour = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/

export default {
  props: {
    value: {
      type: String,
      default: ''
    },
    prefixIcon: {
      type: String,
      default: 'el-icon-time'
    },
    suffixIcon: {
      type: String,
      default: ''
    },
    placeholderI18nKey: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    popperClass: {
      type: String,
      default: 'popover-time'
    },
    is24Hour: {
      type: Boolean,
      default: true
    },
    minHour: {
      type: Number,
      default: 0
    },
    minMinute: {
      type: Number,
      default: 0
    },
    hourStep: {
      type: Number,
      default: 1
    },
    minuteStep: {
      type: Number,
      default: 1
    },
    useScrollSearch: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: '.el-autocomplete-suggestion ul .is-selected {background-color: #d2e6f7;}'
        }
      ]
    }
  },
  data () {
    return {
      reg12Hour: '/^(0?[1-9]|1[0-2]):[0-5][0-9](:(?:[0-5][0-9])?|( |:)?(am|pm|AM|PM))?$|(2[0-3]|[01]?[0-9]):[0-5][0-9]$/',
      reg24Hour: '/^(?:[01]?[0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/',
      selectedTime: null,
      selectHour: null,
      selectMinute: null
    }
  },
  computed: {
    hours () {
      const workHourList = _.range(8, 24, this.hourStep)
      const noWorkHourList = _.range(0, 8, this.hourStep)

      return _.map(_.concat(workHourList, noWorkHourList), (item) => {
        let hour
        if (this.is24Hour) {
          hour = item
        } else {
          if (item === 12) {
            hour = 12
          } else {
            hour = item % 12
          }
        }
        return {
          label: (hour < 10 ? `0${hour}` : `${hour}`),
          value: item
        }
      })
    },
    minutes () {
      const numberList = _.range(0, 60, this.minuteStep)
      return _.map(numberList, (item) => {
        return {
          label: item < 10 ? `0${item}` : `${item}`,
          value: item
        }
      })
    },
    timeOptions () {
      const list = []
      _.forEach(this.hours, (hour) => {
        if (hour.value < this.minHour) {
          return
        }
        let suffixName = ''
        if (!this.is24Hour) {
          suffixName = hour.value > 11 ? ':PM' : ':AM'
        }
        _.forEach(this.minutes, (minute) => {
          if (hour.value === this.minHour && minute.value < this.minMinute) {
            return
          }
          list.push({
            value: `${hour.label}:${minute.label}${suffixName}`,
            valueKey: `${hour.value}:${minute.value}`
          })
        })
      })
      return list
    }
  },
  methods: {
    querySearch (keyword, callback) {
      let results = this.timeOptions

      if (keyword) {
        const keywordStr = keyword.trim()
        const timeRegex = this.is24Hour ? reg24Hour : reg12Hour
        if (!timeRegex.test(keywordStr)) {
          callback(results)
          return
        }
        const time = this.timeStrToTime(keywordStr)

        if (!this.useScrollSearch) {
          results = _.filter(this.timeOptions, item => {
            return _.includes(item.value.toUpperCase(), keywordStr.toUpperCase()) || moment(item.valueKey, 'HH:mm').format('HH:mm') === time.format('HH:mm')
          })
        }
      }
      callback(results)
    },
    handleFocus () {
      this.$nextTick(() => {
        setTimeout(() => this.resetTimeOptions(), 500)
      })
    },
    handleSelect (option) {
      const selectedTime = moment(option.valueKey, 'HH:mm').format('HH:mm:ss')
      this.selectedTime = selectedTime
      this.$emit('change', selectedTime)
    },
    timeStrToTime (timeStr) {
      let parsedTime

      // 如果是12小时制带AM/PM的格式（如"02:05 pm"或"2:05 AM"）
      if (this.is24Hour) {
        // 如果是不带秒数的24小时制格式（如"14:05"或"02:05"）
        parsedTime = moment(timeStr, 'HH:mm')
        // 如果是带有秒数的24小时制格式（如"14:05:30"）
        parsedTime = moment(timeStr, 'HH:mm:ss')
      } else {
        parsedTime = moment(timeStr, ['h:mm A', 'h:mm a', 'hh:mm A', 'hh:mm a'])
      }

      if (parsedTime.isValid()) {
        let hour = parsedTime.hour()
        const minute = parsedTime.minute()
        if (!this.is24Hour && _.includes(timeStr.toUpperCase(), 'PM')) {
          // 如果是12小时制且带PM的格式，需要加上12小时
          hour = hour + 12
        }
        parsedTime = moment(`${hour}:${minute}`, 'HH:mm')
      }
      return parsedTime
    },
    handleClear () {
      if (this.$refs.autocompleteRef) {
        this.$refs.autocompleteRef.activated = true
        this.resetTimeOptions()
      }
    },
    handleChange (value) {
      const valueStr = value.trim()
      const timeRegex = this.is24Hour ? reg24Hour : reg12Hour

      if (!timeRegex.test(valueStr)) {
        this.selectedTime = ''
        this.$emit('change', null)
        return
      }

      const parsedTime = this.timeStrToTime(valueStr)

      if (!parsedTime.isValid()) {
        this.selectedTime = ''
        this.$emit('change', null)
        return
      }

      const hour = parsedTime.hour()
      const minute = parsedTime.minute()
      if (hour < this.minHour || (hour === this.minHour && minute < this.minMinute)) {
        this.selectedTime = ''
        this.$emit('change', null)
        return
      }
      this.selectedTime = valueStr

      this.$emit('change', parsedTime.format('HH:mm:ss'))
    },
    resetTimeOptions () {
      if (!this.$refs.autocompleteRef?.activated) {
        return
      }
      const valueStr = this.selectedTime?.trim()
      if (!valueStr) {
        return
      }
      const parsedTime = this.timeStrToTime(valueStr)
      if (!parsedTime.isValid()) {
        return
      }
      const popoverEl = document.querySelector(`.${this.popperClass}`)
      if (popoverEl && popoverEl.firstChild && popoverEl.firstChild.firstChild) {
        const scrollEl = popoverEl.firstChild.firstChild
        const index = _.findIndex(this.timeOptions, item => {
          return _.includes(item.value.toUpperCase(), valueStr.toUpperCase()) || moment(item.valueKey, 'HH:mm').format('HH:mm') === parsedTime.format('HH:mm')
        })
        let scrollTop = 0
        const selectedEls = scrollEl.querySelectorAll('.is-selected')
        _.forEach(selectedEls, el => {
          el.classList.remove('is-selected')
        })
        if (index >= 0) {
          const selectedEl = scrollEl.firstChild.childNodes[index]
          if (selectedEl) {
            selectedEl.classList.add('is-selected')
          }
          scrollTop = index * 34
        } else {
          scrollTop = 0
        }
        scrollEl.scrollTop = scrollTop
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler () {
        if (this.value) {
          let currentTime = this.timeStrToTime(this.value)
          if (currentTime.isValid()) {
            currentTime = currentTime.locale('en')
            if (this.is24Hour) {
              this.selectedTime = currentTime.format('HH:mm')
            } else {
              const timeStr = currentTime.format('hh:m:a')
              const suffixName = currentTime.hour() > 11 ? 'PM' : 'AM'
              this.selectedTime = timeStr.replace(suffixName.toLowerCase(), suffixName)
            }
          }
        } else {
          this.selectedTime = ''
        }
        this.resetTimeOptions()
      }
    },
    selectedTime: {
      handler () {
        this.resetTimeOptions()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.time-picker {
  .autocomplete-input {
  }
}

.el-autocomplete-suggestion {
  background-color: red;
  ul {
    background-color: blue;
    li {
      &.is-selected {
        background-color: #007bff;
        color: #fff;
      }
    }
  }
}
</style>
